/**
 * A booking's page on the phone website (web-account-pages D8; shared-client-screens D3): the
 * app's booking page (BookingDetailView in its frame), fed the website session's booking, with the
 * app's cancel flow (useBookingCancelFlow) and the app's error screen.
 *
 * The slots the website fills: Back returns where the client came from on this site, or Home (B1);
 * the errand goes to the website's pages (B3, B4, B5); Add to Calendar's choices are plain links and
 * Apple Calendar opens its file through a ticket (B6); Join, maps and links open a new tab (B7);
 * Chat opens the website's messages (B8); the studio's name is the website's (B9). A booking that is
 * not this client's shows the studio's not-found page (B2a, in the page around this).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FocusedScreenLoading } from '@/components/booking/FocusedScreenFrame';
import { MapsChoiceModal } from '@/components/BookingDetail';
import { useToast } from '@/components/Toast/ToastContext';
import { useBookingCancelFlow } from '@/hooks/booking/useBookingCancelFlow';
import { BookingDetailError, BookingDetailFrame, BookingDetailView } from '@/pages/BookingDetailPage/BookingDetailView';
import { bookingCalendarLinks, CALENDAR_FILE_FAILED } from '@/utils/calendarLinks';
import { copyPasscode, joinVirtualMeeting, writeClipboard } from '@/utils/joinVirtual';
import { buildAppleMapsUrl, buildGoogleMapsUrl } from '@/utils/mapLinks';

import { useSiteStudio } from '../api/site';
import { useWebCancelBooking } from '../hooks/useWebAccount';
import { openCalendarFile, useWebBooking } from '../hooks/useWebBooking';
import { useMessagesPanel } from '../messages/panelContext';
import { useSiteBack } from '../pageMover';
import { bookingPageErrand } from '../pieces/booking-page/useBookingPage';

const openOutside = (url: string) => window.open(url, '_blank', 'noopener');

export function PhoneBooking() {
  const navigate = useNavigate();
  const onBack = useSiteBack();
  const { showToast } = useToast();
  const { openMessages } = useMessagesPanel();
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const { detail } = useWebBooking(bookingId);
  const studioName = useSiteStudio().data?.name?.trim() ?? '';
  const b = detail.data;
  const cancelBooking = useWebCancelBooking(bookingId);
  const cancel = useBookingCancelFlow({
    booking: b,
    refetch: detail.refetch,
    cancel: cancelBooking,
    onFailed: () => showToast('This booking could not be cancelled. Please refresh.', 'error'),
  });

  const [mapsOpen, setMapsOpen] = useState(false);
  const [mapsRect, setMapsRect] = useState<DOMRect | null>(null);

  if (!b) {
    return (
      <BookingDetailFrame onBack={onBack}>
        {detail.isError ? (
          <BookingDetailError notFound={false} onBack={onBack} onRetry={() => void detail.refetch()} />
        ) : (
          <FocusedScreenLoading testId="detail-loading" />
        )}
      </BookingDetailFrame>
    );
  }

  // The app's Join: the passcode copied first, then the meeting in a new tab.
  const meeting = { copy: writeClipboard, toast: showToast, open: openOutside };
  const place = { lat: b.location.lat, lng: b.location.lng, address: b.location.address, city: b.location.city, state: b.location.state };
  const errand = bookingPageErrand(b);

  return (
    <BookingDetailFrame onBack={onBack}>
      <BookingDetailView
        booking={b}
        businessName={studioName}
        errand={errand ? { label: errand.label, onClick: () => navigate(errand.href) } : null}
        calendar={{
          choices: bookingCalendarLinks(b),
          onApple: () => void openCalendarFile(b).catch(() => showToast(CALENDAR_FILE_FAILED, 'error')),
        }}
        onJoinVirtual={() => void joinVirtualMeeting(b.location, meeting)}
        onCopyPasscode={() => void copyPasscode(b.location.virtualPassword, meeting)}
        onWhereTap={(rect) => {
          setMapsRect(rect);
          setMapsOpen(true);
        }}
        onChat={() => openMessages()}
        cancel={cancel}
      />
      <MapsChoiceModal
        isOpen={mapsOpen}
        onClose={() => setMapsOpen(false)}
        onSelectGoogle={() => {
          const url = buildGoogleMapsUrl(place);
          if (url) openOutside(url);
        }}
        onSelectApple={() => {
          const url = buildAppleMapsUrl(place);
          if (url) openOutside(url);
        }}
        sourceRect={mapsRect}
        variant="client"
      />
    </BookingDetailFrame>
  );
}
